// REACT IMPORT: Core React library for functional component
import React from "react"

export const PrivacyPolicy = () => {
  return (
    <div className="mx-auto max-w-4xl px-3 py-4 text-white sm:px-4 sm:py-0">
      <div className="rounded-xl bg-white p-4 text-left text-gray-800 shadow-lg sm:rounded-lg sm:p-6 md:p-10">
        <header className="mb-4 text-center sm:mb-8">
          <h1 className="text-2xl font-bold leading-snug text-brand sm:text-3xl md:text-4xl">
            Privacy Policy Agreement
          </h1>
          <p className="mt-2 text-sm font-semibold text-gray-700 sm:mt-3 sm:text-base">
            Effective Date: February 1, 2026
          </p>
        </header>

        <div className="space-y-4 text-sm leading-relaxed sm:space-y-6 sm:text-base md:text-lg">
          <p>
            Sweetest Interactive Entertainment LLC ("Company," "we," "our," or "us") respects your
            privacy and is committed to protecting the information collected through our websites,
            web applications, software services, and related platforms (collectively, the
            "Services").
          </p>

          <p>
            By using our Services, you agree to the collection and use of information in accordance
            with this Privacy Policy.
          </p>

          <section>
            <h2 className="mb-2 text-base font-semibold text-brand sm:mb-3 sm:text-xl">1. User Information</h2>
            <p className="mb-2 sm:mb-3">
              Certain Services may require the collection of limited personal information in order
              to function properly. Other Services may not collect any personal information at all.
            </p>
            <p className="mb-2 sm:mb-3">
              We do not intentionally collect sensitive personal information, including but not
              limited to government-issued identification numbers, precise residential addresses,
              financial account credentials, or any other categories of highly sensitive personal
              data unless expressly required for a specific Service.
            </p>
            <p className="mb-1.5 sm:mb-2">Examples of information that may be collected include:</p>
            <ul className="list-disc space-y-0.5 pl-5 sm:space-y-1 sm:pl-6">
              <li>Name</li>
              <li>Email address</li>
              <li>Authentication identifiers</li>
              <li>User preferences</li>
              <li>Subscription status</li>
              <li>Support communications</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-2 text-base font-semibold text-brand sm:mb-3 sm:text-xl">2. Data Collection Practices</h2>
            <p className="mb-2 sm:mb-3">
              Depending on the specific Service being used, limited information may be collected
              solely for the purpose of providing, maintaining, and improving functionality.
            </p>
            <p className="mb-1.5 sm:mb-2">Collected information may be used for:</p>
            <ul className="mb-2 list-disc space-y-0.5 pl-5 sm:mb-3 sm:space-y-1 sm:pl-6">
              <li>Account authentication</li>
              <li>Access to application features</li>
              <li>Subscription management</li>
              <li>Saving user-generated content</li>
              <li>Performance monitoring</li>
              <li>Error and crash diagnostics</li>
              <li>Customer support</li>
              <li>Security and fraud prevention</li>
            </ul>
            <p>
              We do not sell, rent, trade, or otherwise disclose personal information to third
              parties for commercial marketing purposes.
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-base font-semibold text-brand sm:mb-3 sm:text-xl">3. Third-Party Services</h2>
            <p className="mb-2 sm:mb-3">
              Certain Services may utilize trusted third-party providers to support core
              functionality, including but not limited to:
            </p>
            <ul className="mb-2 list-disc space-y-0.5 pl-5 sm:mb-3 sm:space-y-1 sm:pl-6">
              <li>Authentication providers</li>
              <li>Payment processors</li>
              <li>Analytics services</li>
              <li>Error and crash reporting tools</li>
              <li>Cloud hosting providers</li>
              <li>Email delivery services</li>
            </ul>
            <p className="mb-2 sm:mb-3">
              Examples of such providers may include Supabase, Firebase, Stripe, RevenueCat, Resend,
              Google services, and similar infrastructure providers.
            </p>
            <p>
              These providers operate under their own privacy practices and security standards. We
              do not share personal information with third parties for advertising resale purposes.
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-base font-semibold text-brand sm:mb-3 sm:text-xl">4. Communications</h2>
            <p className="mb-2 sm:mb-3">
              We do not send unsolicited marketing communications.
            </p>
            <p className="mb-1.5 sm:mb-2">Users may receive communications necessary for:</p>
            <ul className="mb-2 list-disc space-y-0.5 pl-5 sm:mb-3 sm:space-y-1 sm:pl-6">
              <li>Account verification</li>
              <li>Password resets</li>
              <li>Subscription management</li>
              <li>Customer support</li>
              <li>Service-related announcements</li>
              <li>Important application updates</li>
            </ul>
            <p>
              Users may opt out of non-essential communications where applicable.
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-base font-semibold text-brand sm:mb-3 sm:text-xl">5. Data Security</h2>
            <p className="mb-2 sm:mb-3">
              We implement reasonable administrative, technical, and organizational safeguards
              designed to protect information from unauthorized access, disclosure, alteration,
              misuse, or destruction.
            </p>
            <p>
              However, no method of electronic transmission or storage is completely secure, and we
              cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-base font-semibold text-brand sm:mb-3 sm:text-xl">6. Data Retention</h2>
            <p className="mb-2 sm:mb-3">
              Information is retained only for as long as reasonably necessary to provide Services,
              comply with legal obligations, resolve disputes, or enforce agreements.
            </p>
            <p>
              Where applicable, users may request deletion of their personal information, subject to
              legal, contractual, or operational requirements.
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-base font-semibold text-brand sm:mb-3 sm:text-xl">7. Children's Privacy</h2>
            <p className="mb-2 sm:mb-3">
              Our Services are not directed toward children under the age required by applicable law
              without parental consent. We do not knowingly collect personal information from
              children.
            </p>
            <p>
              If we become aware that personal information has been collected from a child without
              appropriate consent, we will take reasonable steps to delete such information.
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-base font-semibold text-brand sm:mb-3 sm:text-xl">
              8. Changes to this Privacy Policy
            </h2>
            <p className="mb-2 sm:mb-3">
              Sweetest Interactive Entertainment LLC DBA Sweetest Interactive reserves the
              right to modify or update this Privacy Policy at any time.
            </p>
            <p className="mb-2 sm:mb-3">
              Any changes will be posted with an updated Effective Date. Continued use of the
              Services after any modifications constitutes acceptance of the revised Privacy Policy.
            </p>
            <p>We encourage users to review this Privacy Policy periodically for updates.</p>
          </section>

          <section>
            <h2 className="mb-2 text-base font-semibold text-brand sm:mb-3 sm:text-xl">9. Contact Information</h2>
            <p className="font-semibold">
              Sweetest Interactive Entertainment LLC DBA Sweetest Interactive
            </p>
            <p className="mt-2 sm:mt-3">
              Email:{" "}
              <a
                className="break-all font-semibold text-brand hover:text-orange-dark"
                href="mailto:itsyummtime@sweetestinteractive.com"
              >
                itsyummtime@sweetestinteractive.com
              </a>
            </p>
            <p className="mt-2 sm:mt-3">
              Website:{" "}
              <a
                className="font-semibold text-brand hover:text-orange-dark"
                href="https://sweetestadsmedia.com"
                rel="noreferrer"
                target="_blank"
              >
                sweetestadsmedia.com
              </a>
            </p>
            <p className="mt-2 sm:mt-3">
              For questions regarding this Privacy Policy or requests related to personal
              information, please contact us using the information provided above.
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
